// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useMemo, useState } from 'react';
import { Alert, IconButton, InputBase, Snackbar, Typography } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { UpdateProjectReferenceIdThunk } from 'Features/project/projectReferenceIdThunk';
import stylesDef from './styles';

// Defaults live in the destructuring: defaultProps is deprecated for function components.
/* eslint-disable react/require-default-props */
interface IReferenceIdEditorProps {
  projectId: string;
  referenceId?: string;
  // What an empty value reads as for someone who cannot edit it.
  emptyText?: string;
  // What an empty value reads as for someone who can - clicking it starts an edit.
  addPrompt?: string;
}
/* eslint-enable react/require-default-props */

const SAVE_FAILED_MESSAGE = 'Could not save the Reference ID. Please try again.';

// Who may edit a project's Reference ID on the client: the same createProject right that lets
// a user set it at creation (the server enforces the same rule). Admin builds always may.
const useCanEditReferenceId = (): boolean => {
  const resources = useAppSelector((state) => state.auth.resources);
  return _ADMIN_ ? true : Boolean(resources && 'createProject' in resources);
};

// Keeps the editor's own clicks and keys from reaching a clickable host (the map rail card
// selects its project on click, Enter, and Space).
const swallow = (event: React.SyntheticEvent) => event.stopPropagation();

// A project's Reference ID as inline-editable metadata (AUT-6449): always rendered, empty or
// not; Add / Edit affordance for users with edit rights; Enter saves, Escape cancels; trimmed,
// and clearable back to empty. The displayed value follows the store, so every surface showing
// the project updates together.
const ReferenceIdEditor = ({
  projectId, referenceId, emptyText = 'No reference ID', addPrompt = 'No reference ID — click to add',
}: IReferenceIdEditorProps) => {
  const theme = useTheme();
  const styles = useMemo(() => stylesDef(theme), [theme]);
  const dispatch = useAppDispatch();
  const canEdit = useCanEditReferenceId();
  const currentValue = referenceId || '';

  // The draft being typed (null outside an edit), and the value shown optimistically while its
  // save is in flight (null otherwise).
  const [draft, setDraft] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const isEditing = draft !== null;
  const displayValue = pending === null ? currentValue : pending;

  const startEditing = (event: React.SyntheticEvent) => {
    swallow(event);
    setError(null);
    setDraft(currentValue);
  };

  const cancelEditing = () => {
    setDraft(null);
    setError(null);
  };

  // Optimistic: the new value shows at once and the edit closes; the store catches up on
  // success, and a failure reopens the edit with the attempted value and the reason.
  const save = async () => {
    const nextValue = (draft || '').trim();
    if (nextValue === currentValue) {
      cancelEditing();
      return;
    }
    setPending(nextValue);
    setDraft(null);
    setError(null);
    const result = await dispatch(UpdateProjectReferenceIdThunk({ _id: projectId, referenceId: nextValue }));
    setPending(null);
    if (result.success) {
      setToast(nextValue ? 'Reference ID updated' : 'Reference ID cleared');
    } else {
      setDraft(nextValue);
      setError(result.message || SAVE_FAILED_MESSAGE);
    }
  };

  const onInputKeyDown = (event: React.KeyboardEvent) => {
    swallow(event);
    if (event.key === 'Enter') {
      event.preventDefault();
      save();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelEditing();
    }
  };

  const renderDisplay = () => {
    if (displayValue) {
      return <Typography component="span" sx={styles.value}>{displayValue}</Typography>;
    }
    if (!canEdit) {
      return <Typography component="span" sx={[styles.value, styles.empty]}>{emptyText}</Typography>;
    }
    return (
      <Typography component="span" sx={[styles.value, styles.empty, styles.addPrompt]} onClick={startEditing}>
        {addPrompt}
      </Typography>
    );
  };

  if (!isEditing) {
    return (
      <Box component="span" sx={styles.root}>
        {renderDisplay()}
        {canEdit && (
          <IconButton size="small" sx={styles.iconButton} onClick={startEditing} aria-label={displayValue ? 'Edit reference ID' : 'Add reference ID'}>
            <EditIcon fontSize="inherit" />
          </IconButton>
        )}
        <Snackbar open={Boolean(toast)} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <Box component="span" sx={styles.root} onClick={swallow}>
      <InputBase
        autoFocus
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onInputKeyDown}
        sx={styles.input}
        inputProps={{ 'aria-label': 'Reference ID' }}
      />
      <IconButton size="small" sx={styles.iconButton} onClick={save} aria-label="Save reference ID">
        <CheckIcon fontSize="inherit" />
      </IconButton>
      <IconButton size="small" sx={styles.iconButton} onClick={cancelEditing} aria-label="Cancel editing reference ID">
        <CloseIcon fontSize="inherit" />
      </IconButton>
      {error && <Typography component="span" role="alert" sx={styles.error}>{error}</Typography>}
    </Box>
  );
};

export default ReferenceIdEditor;
